import { DateObject, type FrontAuctionDTO, getTextColorForBackground } from '@capsicum/common';
import { Card, CardBuilder, ModalService, SsuHud, Text, Timer } from '@capsicum/common-web';
import { useRPAuctionCard } from '@capsicum/features/fanshop/shared';
import { useNCWBalance } from '@capsicum/socios-core';
import { TokenParticipationAmount } from '@capsicum/socios-core-web';
import { memo, type PropsWithChildren, useCallback, useMemo } from 'react';
import styled from 'styled-components';

import { BuyFanTokensDrawer } from '../drawers/buy-fan-tokens-drawer/BuyFanTokensDrawer';

type RPAuctionCardProps = PropsWithChildren<{
	'data-tutorial'?: string;
	key?: string;
	onClick?: () => void;
	testID: string;
	variant: FrontAuctionDTO;
}>;

/**
 * RPAuctionCard is specific to each individual auction in the fan shop
 *
 * @param {RPAuctionCardProps} props - The props of the container
 * @param {FrontAuctionDTO} props.variant All data relevant to an auction card
 * @param {string} props.key Unique key per product card
 * @param {string} props.testID the ID to enable e2e framework to grab this element
 * @returns {JSX.Element} The ProductCard
 */

const RPAuctionCardComponent = ({ onClick, testID, variant, ...props }: RPAuctionCardProps) => {
	const { getTokenBalanceBy, isPending: isPendingNCWallet } = useNCWBalance();

	const {
		auctionBlurHash,
		auctionImage,
		auctionPartnerId,
		auctionPrice,
		auctionRibbonColor,
		auctionRibbonText,
		auctionTitle,
		balance,
		bidTitle,
		endAt,
		hasEnoughFanTokens,
		isComingSoon,
		isOwnBid,
		isPendingCWallet,
		isTicketType,
		partnerCurrencyId,
		partnerWallet,
		preventBuyFanTokensModal,
		redeemableAt,
		setCurrentStatus,
		setIsComingSoon,
		texts,
		tokenRequiredAmount
	} = useRPAuctionCard({
		getTokenBalanceBy,
		variant
	});

	const isPendingCombinedWallets = !isPendingCWallet && !isPendingNCWallet;

	const onBuyFanTokensClick = useCallback(
		(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
			e.preventDefault();
			e.stopPropagation();

			if (preventBuyFanTokensModal) return;

			ModalService.addModal({
				renderComponent: ({ close }) => (
					<BuyFanTokensDrawer
						amount={texts.minimumAmount}
						closeModal={close}
						description={texts.getFanTokensText}
						partnerCurrencyId={partnerCurrencyId}
						testID="rp-auction-card-buy-fan-tokens-drawer"
						title={texts.fanTokensNeededText}
						walletAmount={`${balance} ${partnerWallet?.ledger?.currency?.shortCode || ''}`}
					/>
				)
			});
		},
		[
			preventBuyFanTokensModal,
			texts.minimumAmount,
			texts.getFanTokensText,
			partnerCurrencyId,
			texts.fanTokensNeededText,
			balance,
			partnerWallet?.ledger?.currency?.shortCode
		]
	);

	const cardTitle = useMemo(() => {
		return auctionTitle.length > 27 ? auctionTitle.substring(0, 27) + '...' : auctionTitle;
	}, [auctionTitle]);

	const image = useMemo(
		() =>
			auctionImage ? (
				<S.StyledCardImage
					alt={`${variant.label} image`}
					blurhash={auctionBlurHash}
					imageHeight={186}
					optimized
					src={auctionImage}
				/>
			) : (
				<S.StyledCardImagePlaceholder />
			),
		[auctionBlurHash, auctionImage, variant.label]
	);

	const ribbon = useMemo(
		() =>
			isTicketType &&
			auctionRibbonColor && <S.Ribbon $backgroundColor={auctionRibbonColor}>{auctionRibbonText}</S.Ribbon>,
		[auctionRibbonColor, auctionRibbonText, isTicketType]
	);

	const timer = useMemo(() => {
		if (isComingSoon) {
			return (
				<Card.Overlay additionalText={texts.availableIn} variant="full">
					<Timer
						customFormat="digitalTimer"
						expirationDate={new DateObject(redeemableAt!)}
						size="condensed"
						testID={`rp-auctions-${auctionTitle}-digitalTimer`}
						type="default"
						onElapsed={() => setIsComingSoon(false)}
					/>
				</Card.Overlay>
			);
		}

		return null;
	}, [auctionTitle, isComingSoon, redeemableAt, setIsComingSoon, texts.availableIn]);

	const tokenParticipationAmount = useMemo(
		() =>
			!!auctionPartnerId &&
			!hasEnoughFanTokens() &&
			isPendingCombinedWallets && (
				<S.StyledTokenParticipationAmount
					amount={tokenRequiredAmount}
					partnerId={auctionPartnerId}
					testID="rp-auction-card-tokensNeeded-text"
					onClick={onBuyFanTokensClick}
				/>
			),
		[auctionPartnerId, hasEnoughFanTokens, isPendingCombinedWallets, onBuyFanTokensClick, tokenRequiredAmount]
	);

	const ssuPrice = useMemo(
		() => (
			<S.PriceWrapper>
				<S.BidTitle color={isOwnBid ? 'brand.enabled' : 'main.enabled'} variant="caption1">
					{bidTitle()}
				</S.BidTitle>
				<SsuHud size="small" testID="rp-auction-card-ssu-hud">
					{auctionPrice}
				</SsuHud>
			</S.PriceWrapper>
		),
		[auctionPrice, bidTitle, isOwnBid]
	);

	const buildCard = new CardBuilder()
		.setMediaContent(
			partnerWallet && !!auctionPartnerId && (
				<div>
					<S.Wrapper>
						{image}
						{timer}
						{tokenParticipationAmount}
					</S.Wrapper>
					{ribbon}
				</div>
			),
			{ height: 186 }
		)
		.setCardStyle({
			width: 176
		})
		.setOnActivate(() => onClick?.())
		.setProps({ 'data-tutorial': `${props['data-tutorial']}`, testID })
		.setFooter(
			<S.DescriptionWrapper>
				<S.TitleWrapper>{cardTitle}</S.TitleWrapper>
				<S.DescriptionContent>
					<S.TimerWrapper>
						{isComingSoon ? (
							<Timer
								customText={texts.comingSoon}
								expirationDate={undefined}
								size="condensed"
								testID={`rp-auctions-${auctionTitle}-comingSoon`}
							/>
						) : (
							<Timer
								closedLabel
								customFormat="digitalTimer"
								expirationDate={new DateObject(endAt!)}
								size="condensed"
								testID={`rp-auctions-${auctionTitle}-digitalTimer`}
								type="default"
								onElapsed={() => setCurrentStatus('closed')}
							/>
						)}
					</S.TimerWrapper>
					{ssuPrice}
				</S.DescriptionContent>
			</S.DescriptionWrapper>
		)
		.build();

	return buildCard;
};

export const RPAuctionCard = memo(RPAuctionCardComponent);

namespace S {
	export const Wrapper = styled.div`
		position: relative;
	`;

	export const StyledCardImage = styled(Card.Image)`
		height: 186px;
	`;

	export const StyledCardImagePlaceholder = styled.div`
		height: 186px;
	`;

	export const Ribbon = styled.div<{ $backgroundColor: string | undefined }>`
		background-color: ${({ $backgroundColor, theme }) =>
			$backgroundColor || theme.colors.backgrounds.brand.enabled};
		display: flex;
		align-items: center;
		justify-content: center;
		text-transform: capitalize;
		${({ theme }) => theme.fonts.text.text4}
		color: ${({ $backgroundColor, theme }) =>
			getTextColorForBackground($backgroundColor!, 190)
				? theme.colors.white
				: theme.colors.common.scrim.background};
		width: 100%;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		padding: ${({ theme }) => theme.spacings.xxxsmall};
		position: absolute;
		bottom: 0;
		left: 0;
		z-index: 2;
	`;

	export const StyledTokenParticipationAmount = styled(TokenParticipationAmount)`
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: center;
		position: absolute;
		left: ${({ theme }) => theme.spacings.small};
		top: ${({ theme }) => theme.spacings.small};
		z-index: 2;
	`;

	export const DescriptionWrapper = styled.div`
		position: relative;
		height: 117px;
	`;

	export const TitleWrapper = styled.div`
		${({ theme }) => theme.fonts.headings.title6};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const DescriptionContent = styled.div`
		position: absolute;
		bottom: 0;
		margin-top: ${({ theme }) => theme.spacings.xxsmall};
		width: 176px;
	`;

	export const TimerWrapper = styled.div`
		display: flex;
		flex-direction: row;
		justify-content: flex-start;
	`;

	export const PriceWrapper = styled.div`
		margin-top: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const BidTitle = styled(Text)`
		margin-bottom: ${({ theme }) => theme.spacings.xxxsmall};
	`;
}
